import { BO, BusinessObjectBase, FeatureFlagsRulesServiceBase, PropertyChangedJoinPoint, QueryBus, Weaver } from '@skylab-shared/core';
import { DocumentPaymentListItemModel, SaleAdvanceAdjustmentDocumentModel, SaleAdvanceDocumentItemModel, SaleAdvanceDocumentModel, SaleDocumentKind } from '@skylab-shared/dataModel';
import { Calc, MidpointRounding, dateDiffInDays, getMinimalOfDates, substractDays, sum, unique } from '@skylab-shared/utils';
import { ClientRepositoryBase } from '../client/client.repo-base';
import { ContextKeys } from '../common/context-keys';
import { propertyChangedJoinPointFilter } from '../common/property-changed/property-changed-filter';
import { CompanyParametersQuery } from '../company/queries/company-parameters.query-base';
import { CurrencyCollectionQuery } from '../currency/queries/currency.query-base';
import { DocumentExchangeRateValidationBoPart } from '../documents/bo-parts/document-exchange-rate-validation.bo-part';
import { DocumentItemsNumberingBoPart } from '../documents/bo-parts/document-items-numbering.bo-part';
import { DocumentNumberBoPart } from '../documents/bo-parts/document-number.bo-part';
import { DocumentVatRateForSaleValidationBoPart } from '../documents/bo-parts/document-vat-rate-for-sale-validation.bo-part';
import { DocumentWstoBoPart } from '../documents/bo-parts/document-wsto.bo-part';
import { CommunityTransactionForSaleMixin } from '../documents/mixins/community-transaction-for-sale.mixin';
import { ExchangeRateMixin } from '../documents/mixins/exchange-rate.mixin';
import { DocumentLink, DocumentLinks, DocumentLinksQuery } from '../documents/queries/document-links.query-base';
import { NumberingQuery } from '../numbering/queries/numbering.query-base';
import { ProductRepositoryContract } from '../shared-business-temporary-contracts';
import { BusinessDayCalculatorPL } from '../utils/business-days-calculator';
import { VatReleaseReasonQuery } from '../vat/queries/vat-release-reason.query-base';
import { BankAccountNumberMixin } from './mixins/bank-account.mixin';
import { ClientMixin } from './mixins/client.mixin';
import { SaleLinksMixin } from './mixins/sale-links.mixin';
import { TaxLiabilityDateMixin } from './mixins/tax-liability-date.mixin';
import { SaleDocumentCalculatorBoPart } from './parts/sale-document-calculator.bo-part';
import { SaleDocumentClientBoPart } from './parts/sale-document-client.bo-part';
import { SaleDocumentItemsValidationsBoPart } from './parts/sale-document-items-validations.bo-part';
import { SaleDocumentJpkGroupsBoPart } from './parts/sale-document-jpk-groups.bo-part';
import { SaleDocumentKsefBoPart } from './parts/sale-document-ksef.bo-part';
import { SaleDocumentPaymentsBoPart } from './parts/sale-document-payment.bo-part';
import { SaleDocumentReverseChargeAndNPBoPart } from './parts/sale-document-reverse-charge-and-NP.bo-part';
import { SaleDocumentSplitPaymentBoPart } from './parts/sale-document-split-payment.bo-part';
import { SaleDocumentVatReleaseBoPart } from './parts/sale-document-vat-release.bo-part';
import { SaleDocumentWithDueDateBoPart } from './parts/sale-document-with-due-date.bo-part';
import { AdvanceSummaryQuery } from './queries/advance-summary.query-base';
import { SaleAdvancePlainObjectQuery } from './queries/sale-advance-plain-object.query-base';
import { SaleKindQuery } from './queries/sale-kind.query-base';
import { rewritePlainObjectToAdvanceDocumentModel } from './utils/sale-rewriting-utils';
import { refreshSaleDocumentItemPriceAsync } from './utils/sale-utils';
import { AdvanceReturnValueGreaterThanAdvanceSummaryValueError } from './violations/advance-return-value-greater-than-advance-summary-value-error';
import { AdvanceReturnValueLessThanZeroError } from './violations/advance-return-value-less-than-zero-error';
import { AmountOfPaymentGreaterThanAdvanceReturnValueError } from './violations/amount-of-payment-greater-than-advance-return-value-error';
import { DateIncludedInAccountingWarning } from './violations/date-included-in-accounting-warning';
import { IssueDateEarlierThanAdjustedError } from './violations/issue-date-earlier-than-adjusted-error';
import { WrongFirstAdvanceDocumentIdError } from './violations/wrong-first-advance-document-id-error';
import { WrongFirstAdvanceDocumentVersionError } from './violations/wrong-first-advance-document-version-error';

export interface SaleAdvanceAdjustmentBO extends ClientMixin<SaleAdvanceAdjustmentDocumentModel>, ExchangeRateMixin<SaleAdvanceAdjustmentDocumentModel>, CommunityTransactionForSaleMixin<SaleAdvanceAdjustmentDocumentModel>, SaleLinksMixin<SaleAdvanceAdjustmentDocumentModel>, TaxLiabilityDateMixin<SaleAdvanceAdjustmentDocumentModel>, BankAccountNumberMixin<SaleAdvanceAdjustmentDocumentModel> { };

@BO({
    modelType: SaleAdvanceAdjustmentDocumentModel,
    mixins: [ExchangeRateMixin, CommunityTransactionForSaleMixin, SaleLinksMixin, TaxLiabilityDateMixin, BankAccountNumberMixin, ClientMixin]
})
export class SaleAdvanceAdjustmentBO extends BusinessObjectBase<SaleAdvanceAdjustmentDocumentModel> {
    private adjustedDocument!: SaleAdvanceDocumentModel;

    public constructor(
        weaver: Weaver,
        protected readonly clientRepository: ClientRepositoryBase,
        protected readonly featureFlagsRulesService: FeatureFlagsRulesServiceBase,
        protected readonly queryBus: QueryBus,
        protected readonly productRepository: ProductRepositoryContract
    ) {
        super(weaver);
        this.initBOParts();
    }

    private initBOParts(): void {
        this.parts.push(...[
            new SaleDocumentItemsValidationsBoPart(this.weaver, this.productRepository, this.featureFlagsRulesService, this.queryBus),
            new DocumentVatRateForSaleValidationBoPart(this.weaver),
            new DocumentItemsNumberingBoPart(this.weaver),
            new SaleDocumentCalculatorBoPart(this.weaver),
            new SaleDocumentWithDueDateBoPart(this.weaver),
            new SaleDocumentPaymentsBoPart(this.weaver),
            new SaleDocumentClientBoPart(this.weaver, this.featureFlagsRulesService),
            new SaleDocumentSplitPaymentBoPart(this.weaver, this.productRepository, this.featureFlagsRulesService),
            new SaleDocumentReverseChargeAndNPBoPart(this.weaver),
            new DocumentNumberBoPart(this.weaver, this.queryBus),
            new DocumentWstoBoPart(this.weaver, this.queryBus),
            new SaleDocumentJpkGroupsBoPart(this.weaver, this.featureFlagsRulesService),
            new SaleDocumentVatReleaseBoPart(this.weaver, this.queryBus),
            new SaleDocumentKsefBoPart(this.weaver, () => this.adjustedDocument),
            new DocumentExchangeRateValidationBoPart(this.weaver)
        ]);
    }

    protected override async initializeAsync(): Promise<void> {
        await super.initializeAsync();

        await Promise.resolve([
            this.localContext.preload(CurrencyCollectionQuery),
            this.localContext.preload(SaleKindQuery),
            this.localContext.preload(CompanyParametersQuery),
            this.localContext.preload(NumberingQuery),
            this.localContext.preload(VatReleaseReasonQuery)
        ]);

        if (this.model.isPersistent) {
            await this.loadSourceClient();
            await this.loadLinksAsync();

            const lastAdvanceLink = await this.getLastAdvanceLink();
            await this.setAdjustedDocumentAsync(this.model.adjustedDocumentId ?? lastAdvanceLink.documentId); // in order to avoid db migration

            this.updateFirstDocumentProperties(this.links);
        }

        this.registerValidationRules();
        this.registerReadonlyRules();
        this.registerPropretyChanged();
    }

    private registerDatesEarlierThanAdjustedRules(): void {
        this.validation.addRule(
            [[SaleAdvanceAdjustmentDocumentModel, 'issueDate']],
            ctx => {
                if (this.adjustedDocument) {
                    const currentIssueDate = new Date(ctx.source.issueDate);
                    const adjustedIssueDate = new Date(this.adjustedDocument.issueDate);
                    return dateDiffInDays(currentIssueDate, adjustedIssueDate) >= 0;
                }
                return true;
            },
            IssueDateEarlierThanAdjustedError
        );
    }

    private registerTaxLiabilityDateValidationRules(): void {
        this.validation.addRule(
            [[SaleAdvanceAdjustmentDocumentModel, 'taxLiabilityDate']],
            async () => {
                if (this.model.issuedByVatPayer) {
                    return !(await this.isTaxLiabilityDateIncludedInAccountancyAsync());
                }
                return true;
            },
            DateIncludedInAccountingWarning
        );
    }

    private registerAdvanceReturnValueValidationRules(): void {
        this.validation.addRule(
            [[SaleAdvanceAdjustmentDocumentModel, 'advanceReturnValue']],
            async ctx => {
                if (this.adjustedDocument) {
                    const advanceSummary = await this.calculateTotalAdvanceAsync();
                    return !(advanceSummary < ctx.bo.model.advanceReturnValue);
                }
                return true;
            },
            AdvanceReturnValueGreaterThanAdvanceSummaryValueError
        );
        this.validation.addRule(
            [[SaleAdvanceAdjustmentDocumentModel, 'advanceReturnValue']],
            async ctx => {
                if (this.adjustedDocument) {
                    return !(ctx.source.advanceReturnValue < 0);
                }
                return true;
            },
            AdvanceReturnValueLessThanZeroError
        );
    }

    private registerAmountOfPaymentsValidationRule(): void {
        this.validation.addRule(
            [[SaleAdvanceAdjustmentDocumentModel, 'advanceReturnValue']],
            [[DocumentPaymentListItemModel, 'value']],
            ctx => {
                return ctx.target === ctx.bo.model.payments[ctx.bo.model.payments.length - 1]
                    ? !(ctx.bo.paid > ctx.bo.model.advanceReturnValue)
                    : true;
            },
            AmountOfPaymentGreaterThanAdvanceReturnValueError
        );
    }

    private registerWrongFirstAdvanceDocumentDetails(): void {
        this.validation.addRule(
            [[SaleAdvanceAdjustmentDocumentModel, 'firstAdvanceDocumentId']],
            ctx => {
                if (this.adjustedDocument) {
                    return ctx.bo.model.firstAdvanceDocumentId !== null;
                }
                return true;
            },
            WrongFirstAdvanceDocumentIdError
        );

        this.validation.addRule(
            [[SaleAdvanceAdjustmentDocumentModel, 'firstAdvanceDocumentVersion']],
            ctx => {
                if (this.adjustedDocument) {
                    return ctx.bo.model.firstAdvanceDocumentVersion !== null;
                }
                return true;
            },
            WrongFirstAdvanceDocumentVersionError
        );
    }

    private registerReadonlyRules(): void {
        this.readonly.setAsReadonly([SaleAdvanceAdjustmentDocumentModel, 'inventoryEffect']);

        this.readonly.addRule([
            [SaleAdvanceAdjustmentDocumentModel, 'client'],
            [SaleAdvanceAdjustmentDocumentModel, 'calculationMethod'],
            [SaleAdvanceAdjustmentDocumentModel, 'jpkVatGroups'],
            [SaleAdvanceAdjustmentDocumentModel, 'currencyId'],
            [SaleAdvanceAdjustmentDocumentModel, 'communityTransactionId'],
            [SaleAdvanceAdjustmentDocumentModel, 'rateNP'],
            [SaleAdvanceAdjustmentDocumentModel, 'reverseCharge'],
            [SaleAdvanceAdjustmentDocumentModel, 'orderNumber'],
            [SaleAdvanceAdjustmentDocumentModel, 'orderDate'],
            [SaleAdvanceAdjustmentDocumentModel, 'agreementNumber'],
            [SaleAdvanceAdjustmentDocumentModel, 'agreementDate']],
            () => true
        );
        this.readonly.setReadonlyOnSubtree(SaleAdvanceDocumentItemModel, () => true, {
            exceptions: [
                [SaleAdvanceDocumentItemModel, <keyof SaleAdvanceDocumentItemModel>'note']
            ]
        });

        this.readonlyChecker.addOmitRule([
            [SaleAdvanceAdjustmentDocumentModel, 'jpkVatGroups']
        ]);
    }

    private registerPropretyChanged(): void {
        this.updateBankAccountNumberAfterPaymentMethodChange();
        this.synchronizeJpkVatGroupsOnAdvanceReturnValueChange();
    }

    private registerValidationRules(): void {
        this.registerDatesEarlierThanAdjustedRules();
        this.registerTaxLiabilityDateValidationRules();
        this.registerAdvanceReturnValueValidationRules();
        this.registerAmountOfPaymentsValidationRule();
        this.registerWrongFirstAdvanceDocumentDetails();
    }

    protected calculateDateForCurrencyExchangeRate(): Date {
        const date = this.model.issuedByVatPayer
            ? getMinimalOfDates(this.model.issueDate, this.model.taxLiabilityDate)
            : dateDiffInDays(this.model.issueDate, this.model.saleDate!) < 0 ? this.model.issueDate : this.model.saleDate;

        return BusinessDayCalculatorPL.getLastBusinessDay(substractDays(date!, 1));
    }

    private updateFirstDocumentProperties(links: DocumentLinks): void {
        this.model.firstAdvanceDocumentId = links.advances.filter(x => x.documentId < links.current.documentId).length > 0 ? links.advances[0].documentId : null as any;
        this.model.firstAdvanceDocumentVersion = links.advances.filter(x => x.documentId < links.current.documentId).length > 0 ? links.advances[0].version : null as any;
    }

    public async setAdjustedDocumentAsync(adjustedDocumentId: number): Promise<SaleAdvanceDocumentModel> {
        this.model.adjustedDocumentId = adjustedDocumentId;

        const plainObject = await this.localContext.getAsync(ContextKeys.document(adjustedDocumentId), () => this.queryBus.queryAsync(new SaleAdvancePlainObjectQuery(), adjustedDocumentId));

        return this.adjustedDocument = rewritePlainObjectToAdvanceDocumentModel(plainObject);
    }

    public async calculateTotalAdvanceAsync(): Promise<number> {
        const lastAdvanceLink = await this.getLastAdvanceLink();
        const advanceSummary = await this.localContext.getAsync(ContextKeys.advanceSummary(lastAdvanceLink.documentId), () => this.queryBus.queryAsync(new AdvanceSummaryQuery(lastAdvanceLink.documentId)));
        this.model.vatSummary = advanceSummary;

        const { precision } = this.localContext.firstPreloaded(CurrencyCollectionQuery, { id: this.model.currencyId });
        return Calc.round(sum(this.adjustedDocument.vatTableItems.asArray(), i => i.grossValue), precision, MidpointRounding.AwayFromZero);
    }

    public async getLastAdvanceLink(): Promise<DocumentLink> {
        const documentId = this.adjustedDocument ? this.adjustedDocument.id : this.model.id;
        const links = this.model.isPersistent ? this.links : await this.localContext.getAsync(ContextKeys.documentLinks(documentId), () => this.queryBus.queryAsync(new DocumentLinksQuery(documentId)));
        const [lastAdvance] = links.advances.filter(advanceLink => advanceLink.kind === SaleDocumentKind.AdvanceInvoice).slice(-1);
        return lastAdvance;
    }

    public async getAdvanceLink(): Promise<DocumentLink> {
        const documentId = this.adjustedDocument ? this.adjustedDocument.id : this.model.id;
        const links = this.model.isPersistent ? this.links : await this.localContext.getAsync(ContextKeys.documentLinks(documentId), () => this.queryBus.queryAsync(new DocumentLinksQuery(documentId)));
        const [lastAdvance] = links.advances.filter(advanceLink => advanceLink.kind === SaleDocumentKind.AdvanceInvoice && advanceLink.documentId === documentId);
        return lastAdvance;
    }

    private updateBankAccountNumberAfterPaymentMethodChange(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(SaleAdvanceAdjustmentDocumentModel, ['paymentMethodId'])(
            _ => this.updateBankAccountNumber()
        )));
    }

    private synchronizeJpkVatGroupsOnAdvanceReturnValueChange(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(SaleAdvanceAdjustmentDocumentModel, ['advanceReturnValue'])(
            () => {
                const shouldBeAdded = this.model.advanceReturnValue > 0;
                const itemsGroups = this.model.items.map(elem => elem.jpkVatGroup).filter(elem => elem);
                this.model.jpkVatGroups = shouldBeAdded
                    ? unique([...this.model.jpkVatGroups, ...itemsGroups])
                    : this.model.jpkVatGroups.filter(elem => !itemsGroups.includes(elem));
            }
        )));
    }

    public get remainingAmountToPay(): number {
        const { precision } = this.localContext.firstPreloaded(CurrencyCollectionQuery, { id: this.model.currencyId })!;
        const remaining = Calc.round(Math.abs(this.model.advanceReturnValue) - this.paid, precision, MidpointRounding.AwayFromZero);
        return remaining;
    }

    public get paid(): number {
        if (this.model.currencyId === 0) {
            return 0;
        }
        const { precision } = this.localContext.firstPreloaded(CurrencyCollectionQuery, { id: this.model.currencyId })!;
        const paid = Calc.round(this.model.payments.reduce((sum, { value }) => sum + value, 0), precision, MidpointRounding.AwayFromZero);

        return paid;
    }

    public async refreshDocumentItemsPricesAsync(): Promise<void> {
        await Promise.all(this.model.items.map(async item => await refreshSaleDocumentItemPriceAsync(item, this, this.productRepository)));
    }

    public get previousCommunityTransactionId(): number {
        return this.adjustedDocument.communityTransactionId;
    }
}
